/**
 * Usage Warning Banner Component
 * Displays critical usage warnings to users
 * Mandatory: Shows at 70%, 90%, and 100% usage thresholds
 */

import React from "react";
import {
  getWarningClassName,
  getWarningIcon,
  shouldBlockActions,
} from "../../services/usageWarningService";
import { ActiveUsageWarning } from "../../types/billing.types";
import "./UsageWarningBanner.css";

// Re-export split components for backward compatibility
export { UsageProgressBar } from "./UsageProgressBar";
export { UsageThresholdIndicator } from "./UsageThresholdIndicator";
export { UsageWarningStack } from "./UsageWarningStack";
export { UsageWarningState } from "./UsageWarningState";

interface UsageWarningBannerProps {
  warnings: ActiveUsageWarning[];
  onDismiss: (warningId: string) => void;
  onAction?: (actionUrl: string) => void;
  isVisible?: boolean;
}

/**
 * Primary warning banner component
 * Displays one warning at a time (highest severity first)
 */
export const UsageWarningBanner: React.FC<UsageWarningBannerProps> = ({
  warnings,
  onDismiss,
  onAction,
  isVisible = true,
}) => {
  const primaryWarning = warnings[0];

  if (!primaryWarning || !isVisible) {
    return null;
  }

  const icon = getWarningIcon(primaryWarning.level);
  const className = getWarningClassName(primaryWarning.level);
  const isBlocking = shouldBlockActions(primaryWarning.level);

  return (
    <div
      className={className}
      role="alert"
      aria-live="polite"
      data-testid="usage-warning-banner"
    >
      <div className="warning-content">
        <span className="warning-icon" aria-hidden="true">
          {icon}
        </span>
        <div className="warning-text">
          <p className="warning-message">{primaryWarning.message}</p>
          {isBlocking && (
            <p className="warning-severity">
              ⚠️ Service interruption may occur if not addressed immediately.
            </p>
          )}
        </div>
      </div>

      <div className="warning-actions">
        {primaryWarning.actionLabel && primaryWarning.actionUrl && (
          <button
            className="btn-warning-action btn-primary"
            onClick={() => onAction?.(primaryWarning.actionUrl!)}
            data-testid={`btn-${primaryWarning.threshold}-action`}
          >
            {primaryWarning.actionLabel}
          </button>
        )}
        <button
          className="btn-dismiss"
          onClick={() => onDismiss(primaryWarning.warningId)}
          aria-label="Dismiss warning"
          data-testid="btn-dismiss-warning"
        >
          ✕
        </button>
      </div>

      {warnings.length > 1 && (
        <div className="warning-badge">+{warnings.length - 1} more</div>
      )}
    </div>
  );
};
